import {
  CalendarClock,
  CalendarOff,
  CalendarX,
  CircleAlert,
  DollarSign,
  History,
  Plus,
  Trash2,
  TrendingUp,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import {
  CampoDeBusqueda,
  ConfirmModal,
  DataTable,
  FilterBar,
  FiltroSelect,
  FiltroToggle,
  PageHeader,
  Paginacion,
  StatsCards,
} from "../../components/ui/patrones";
import { Aviso, Boton, Campo, Etiqueta, Modal } from "../../components/ui/primitivos";
import {
  borrarPrecio,
  cerrarVigencia,
  crearPrecio,
  fetchPrecios,
  fetchProducto,
  fetchProductos,
  type PrecioListado,
} from "../../lib/consola-api";
import { fecha, precioUnitario } from "../../lib/formato";
import { type Orden, ordenarLista, siguienteOrden } from "../../lib/orden";
import { paginar, paginaValida } from "../../lib/paginacion";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";
import { HistorialPrecios } from "./HistorialPrecios";
import { fondoDeFila, opcionesDeCombinacion } from "./precios";

/**
 * Lista de Precios (ADR 0010, 0012, 0023) — porteada de `PreciosList.tsx` de
 * `imprenta`, con su nombre.
 *
 * Un precio **no se edita**: se cierra el vigente y se abre otro. Por eso la
 * tabla no tiene botón "Editar" sino "Cerrar vigencia", y el formulario de alta
 * pide siempre desde cuándo rige. Es la regla que hace que un presupuesto viejo
 * siga explicándose con el precio que tenía.
 *
 * De allá vienen las **tarjetas que filtran**, los **cuatro filtros** —producto,
 * tamaño, base y estado—, la búsqueda y el historial por combinación.
 *
 * **La importación de precios no es una pantalla aparte.** `imprenta` tiene
 * `ImportPreciosPage`, pero monta la misma maquinaria que `ImportProductosPage`
 * —mismo store, mismo endpoint— porque una planilla de precios ES una planilla
 * de productos. Acá se importa desde Productos, y no hay nada que duplicar.
 */

const ESTADOS = { vigente: "ok", futuro: "aviso", expirado: "neutral" } as const;

export function PreciosPagina() {
  const navegar = useNavigate();
  const { can } = useSession();
  const [producto, setProducto] = useState("");
  /**
   * Qué está mirando la pantalla.
   *
   * Tres de los focos son estados de vigencia y **sincronizan con el `<select>`
   * de la barra** (ADR 0089). Los otros dos —`cambian` y `vencen`— son
   * preguntas que la vigencia no contesta, y `sinPrecio` ni siquiera mira la
   * tabla de precios: mira las combinaciones que NO están en ella.
   *
   * Un solo estado y no cinco banderas: dos focos a la vez no significan nada
   * —«vigentes que además vencen sin reemplazo» ya es una de las tarjetas— y
   * con banderas independientes la pantalla queda mostrando una intersección
   * que nadie pidió.
   */
  const [foco, setFoco] = useState("");
  const estado = ["vigente", "futuro", "expirado"].includes(foco) ? foco : "";
  const [tamano, setTamano] = useState("");
  const [base, setBase] = useState("");
  const [busqueda, setBusqueda] = useState("");
  // El estado se filtra del lado cliente para que las tarjetas puedan mostrar
  // el conteo de CADA uno; el producto sí va al servidor, que es el que puede
  // achicar de verdad el conjunto.
  // `incluirInactivos` va al SERVIDOR: los precios de un producto dado de baja
  // no vienen salvo que se pidan, como el `include_inactive` de `imprenta`.
  const [inactivos, setInactivos] = useState(false);
  const datos = useDatos(
    () => fetchPrecios(producto, "", { incluirInactivos: inactivos }),
    `${producto}|${inactivos}`,
  );
  const productos = useDatos(() => fetchProductos());
  const [creando, setCreando] = useState(false);
  const [cerrando, setCerrando] = useState<PrecioListado | null>(null);
  const [historial, setHistorial] = useState<PrecioListado | null>(null);
  const [borrando, setBorrando] = useState<PrecioListado | null>(null);
  const [orden, setOrden] = useState<Orden | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);
  const [tamanoPagina, setTamanoPagina] = useState(20);

  const todos = datos.datos?.precios ?? [];
  const resumen = datos.datos?.resumen;
  const sinPrecio = datos.datos?.combinacionesSinPrecio ?? [];

  // Las opciones salen de los precios cargados y no del catálogo: ofrecer un
  // tamaño que no tiene ni un precio deja filtrar hacia una tabla vacía.
  const { tamanos, bases } = opcionesDeCombinacion(todos);

  const texto = busqueda.trim().toLowerCase();
  const coincide = (campos: string[]) =>
    !texto || campos.some((campo) => campo.toLowerCase().includes(texto));

  const visibles = todos.filter((p) => {
    if (estado && p.estado !== estado) return false;
    // Los dos focos que la vigencia no contesta. Las banderas las calcula el
    // worker, no la pantalla: el número de la tarjeta y las filas que se ven
    // tienen que salir de la misma cuenta (ADR 0090).
    if (foco === "cambian" && !p.cambiaEnMes) return false;
    if (foco === "vencen" && !p.venceSinReemplazo) return false;
    if (tamano && p.tipoTamanoId !== tamano) return false;
    if (base && p.tipoBaseId !== base) return false;
    return coincide([p.producto, p.tamano, p.base]);
  });

  /**
   * Las combinaciones sin precio, con los mismos filtros que la tabla.
   *
   * Se muestran **en lugar** del listado, no dentro: son filas que no existen
   * en la tabla de precios, y meterlas ahí obligaría a inventar una fila vacía
   * con un precio que no hay.
   */
  const sinPrecioVisibles = sinPrecio.filter(
    (c) =>
      (!tamano || c.tipoTamanoId === tamano) &&
      (!base || c.tipoBaseId === base) &&
      coincide([c.producto, c.tamano, c.base]),
  );
  const mostrandoSinPrecio = foco === "sinPrecio";

  /**
   * Qué mira cada columna ordenable. El precio y la cantidad se comparan como
   * NÚMEROS: vienen como texto desde el worker, y comparados como texto `"100"`
   * queda antes que `"99"`.
   */
  const ordenadas = ordenarLista(visibles, orden, (p, campo) => {
    switch (campo) {
      case "producto":
        return p.producto;
      case "tamano":
        return p.tamano;
      case "base":
        return p.base;
      case "cantidad":
        return p.cantidadDesde;
      case "precio":
        return Number(p.precio);
      case "desde":
        return p.vigenteDesde;
      case "hasta":
        // `null` es «indefinido», y `ordenarLista` manda los vacíos al final en
        // las dos direcciones: un precio sin vencimiento no es el que vence
        // primero ni el que vence último, es el que no vence.
        return p.vigenteHasta;
      case "estado":
        return p.estado;
      default:
        return null;
    }
  });

  const filtrosActivos =
    (producto ? 1 : 0) +
    (estado ? 1 : 0) +
    (tamano ? 1 : 0) +
    (base ? 1 : 0) +
    (texto ? 1 : 0) +
    (inactivos ? 1 : 0);

  // Veinte por página, como la tabla de allá. La página se recorta a lo que
  // existe: al filtrar estando en la 4, quedarse ahí muestra una tabla vacía
  // con el paginador diciendo que hay filas.
  const paginaActual = paginaValida(pagina, ordenadas.length, tamanoPagina);
  const enPantalla = paginar(ordenadas, paginaActual, tamanoPagina);

  async function accion(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await datos.recargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "La acción falló");
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        titulo="Lista de Precios"
        subtitulo="Consulta los precios de impresión por producto, tamaño y base."
        acciones={
          /* Los dos van con su ícono adelante, como todos los de `imprenta`
             (ADR 0091): allá «Aumentar Precios» lleva la tendencia y los de
             alta llevan el `+`. El nombre también es el suyo — acá decía
             «Aumento masivo», que es la misma acción con otro nombre.

             Alta en azul y aumento en contorno, que es cómo `imprenta` separa
             la acción principal de la de al lado. En SU Lista de Precios no hay
             botón azul porque no se puede crear un precio suelto: se importa o
             se aumenta. Acá sí se puede, y hace falta —un precio no se edita
             (ADR 0012), se cierra y se abre otro—, así que el azul es de este
             botón. */
          <>
            {can("precios", "bulk_update") ? (
              <Boton variante="secundario" onClick={() => navegar("/precios/aumentar")}>
                <TrendingUp className="mr-2 inline h-4 w-4" aria-hidden="true" />
                Aumentar Precios
              </Boton>
            ) : null}
            {can("precios", "create") ? (
              <Boton onClick={() => setCreando(true)}>
                <Plus className="mr-2 inline h-4 w-4" aria-hidden="true" />
                Nuevo Precio
              </Boton>
            ) : null}
          </>
        }
      />

      {error ? <Aviso>{error}</Aviso> : null}
      {datos.error ? <Aviso>{datos.error}</Aviso> : null}

      {/* Las cinco tarjetas contestan la pregunta de quien cotiza —«¿que no
          puedo cotizar y que se me vence?»— y cuatro de ellas ademas filtran
          (ADR 0090). El embudo dice cuales.

          Los textos son cortos a proposito: cada uno entra en UN renglon al
          ancho al que quedan cinco tarjetas. Si uno se estira a dos, esa
          tarjeta pide mas alto y las cinco crecen juntas —`h-full` las iguala—,
          y una franja de tarjetas mas alta empuja la tabla hacia abajo sin
          decir nada nuevo. */}
      <StatsCards
        estadisticas={[
          {
            titulo: "Vigentes hoy",
            valor: resumen?.vigentes ?? 0,
            descripcion: "Lo que se cobra hoy",
            icono: DollarSign,
            activa: foco === "vigente",
            alClickear: () => setFoco(foco === "vigente" ? "" : "vigente"),
          },
          {
            titulo: "Sin precio vigente",
            valor: resumen?.sinPrecioVigente ?? 0,
            descripcion: "No se pueden cotizar",
            icono: CircleAlert,
            tono: "alerta",
            activa: foco === "sinPrecio",
            alClickear: () => setFoco(foco === "sinPrecio" ? "" : "sinPrecio"),
          },
          {
            titulo: "Cambian este mes",
            valor: resumen?.cambianEnMes ?? 0,
            descripcion: "Empiezan a regir en 30 días",
            icono: CalendarClock,
            activa: foco === "cambian",
            alClickear: () => setFoco(foco === "cambian" ? "" : "cambian"),
          },
          {
            titulo: "Vencen sin reemplazo",
            valor: resumen?.vencenSinReemplazo ?? 0,
            descripcion: "Caen en 30 días sin sucesor",
            icono: CalendarX,
            tono: "aviso",
            activa: foco === "vencen",
            alClickear: () => setFoco(foco === "vencen" ? "" : "vencen"),
          },
          {
            // La unica que NO filtra, y por eso no lleva embudo: esa ausencia
            // es la que ensena que las otras cuatro si.
            titulo: "Último aumento",
            // El porcentaje arriba y la fecha abajo, y no al reves: el numero
            // grande de las otras cuatro es la magnitud, y acá la magnitud es
            // cuanto subio. Ademas una fecha con el porcentaje al lado no entra
            // en un renglon a `text-2xl`, y esa tarjeta quedaba mas alta que
            // las demas.
            valor: resumen?.ultimoAumento ? `+${Number(resumen.ultimoAumento.porcentaje)}%` : "—",
            descripcion: resumen?.ultimoAumento
              ? `${fecha(resumen.ultimoAumento.fecha)} · ${resumen.ultimoAumento.precios} ${resumen.ultimoAumento.precios === 1 ? "precio" : "precios"}`
              : "Sin aumentos masivos",
            icono: TrendingUp,
          },
        ]}
      />

      <FilterBar
        filtrosActivos={filtrosActivos}
        alLimpiar={() => {
          setProducto("");
          setFoco("");
          setTamano("");
          setBase("");
          setBusqueda("");
          setInactivos(false);
        }}
        busqueda={
          <CampoDeBusqueda
            valor={busqueda}
            alCambiar={setBusqueda}
            placeholder="Buscar por producto, tamaño, base..."
          />
        }
      >
        <FiltroSelect
          etiqueta="Todos los"
          sustantivo="productos"
          valor={producto}
          alCambiar={(v) => {
            setProducto(v);
            // El tamaño y la base son de OTRO producto: dejarlos puestos deja
            // la tabla vacía sin que se entienda por qué.
            setTamano("");
            setBase("");
          }}
          opciones={(productos.datos ?? []).map((p) => ({ valor: p.id, texto: p.nombre }))}
          ancho="11.25rem"
        />
        <FiltroSelect
          etiqueta="Todos los"
          sustantivo="tamaños"
          valor={tamano}
          alCambiar={setTamano}
          opciones={tamanos.map(([id, nombre]) => ({ valor: id, texto: nombre }))}
          ancho="10rem"
        />
        <FiltroSelect
          etiqueta="Todas las"
          sustantivo="bases"
          valor={base}
          alCambiar={setBase}
          opciones={bases.map(([id, nombre]) => ({ valor: id, texto: nombre }))}
          ancho="9.375rem"
        />
        {/* La vigencia está en la barra Y en las tarjetas, y es el MISMO
            estado: tocar una tarjeta mueve el filtro y elegir acá enciende la
            tarjeta. Dos controles que dicen cosas distintas sobre lo mismo es
            peor que no tener uno. */}
        <FiltroSelect
          etiqueta="Todas las"
          sustantivo="vigencias"
          valor={estado}
          alCambiar={setFoco}
          opciones={[
            { valor: "vigente", texto: "Vigentes" },
            { valor: "futuro", texto: "Futuros" },
            { valor: "expirado", texto: "Expirados" },
          ]}
          ancho="10.625rem"
        />
        <FiltroToggle
          etiqueta="Productos"
          sustantivo="Inactivos"
          activo={inactivos}
          alCambiar={setInactivos}
        />
      </FilterBar>

      {mostrandoSinPrecio ? (
        /* Otra tabla, no la misma filtrada: estas filas NO son precios — son
           combinaciones que no tienen ninguno. Meterlas en el listado obligaria
           a inventar una fila con un precio que no existe. */
        <DataTable
          datos={sinPrecioVisibles}
          clave={(c) => `${c.tamanoId}|${c.baseId}`}
          cargando={datos.cargando}
          mensajeVacio="Todas las combinaciones tienen precio vigente"
          columnas={[
            { clave: "producto", encabezado: "Producto", render: (c) => c.producto },
            {
              clave: "tamano",
              encabezado: "Tamaño",
              render: (c) => c.tamano,
            },
            {
              clave: "base",
              encabezado: "Base",
              render: (c) => c.base,
            },
            {
              clave: "ultimo",
              encabezado: "Se cotizaba hasta",
              className: "text-neutral-600",
              // Distinguir «vencio» de «nunca tuvo» es la diferencia entre un
              // precio que hay que renovar y uno que nunca se cargo.
              render: (c) =>
                c.ultimoVencimiento === null ? "nunca tuvo precio" : fecha(c.ultimoVencimiento),
            },
          ]}
        />
      ) : (
        <DataTable
          datos={enPantalla}
          clave={(p) => p.id}
          cargando={datos.cargando}
          mensajeVacio="No hay precios"
          // Un precio programado y uno vencido se leen igual en la tabla y
          // significan lo contrario: allá el fondo de la fila los distingue.
          claseDeFila={(p) => fondoDeFila(p.estado)}
          orden={orden}
          alOrdenar={(campo) => setOrden((o) => siguienteOrden(o, campo))}
          columnas={[
            {
              clave: "producto",
              ordenarPor: "producto",
              encabezado: "Producto",
              className: "font-medium",
              // Los expirados se atenúan: siguen siendo historia consultable.
              render: (p) => (
                <span className={p.estado === "expirado" ? "opacity-60" : ""}>{p.producto}</span>
              ),
            },
            // Tamaño y base en DOS columnas, como `imprenta`: juntas en una no
            // se pueden ordenar por separado, y ordenar por base es lo que
            // contesta «¿qué precios tengo cargados para la base negra?».
            {
              clave: "tamano",
              ordenarPor: "tamano",
              encabezado: "Tamaño",
              className: "text-neutral-600",
              render: (p) => p.tamano,
            },
            {
              clave: "base",
              ordenarPor: "base",
              encabezado: "Base",
              className: "text-neutral-600",
              render: (p) => p.base,
            },
            {
              clave: "cantidad",
              ordenarPor: "cantidad",
              encabezado: "Cantidad",
              className: "text-neutral-600",
              render: (p) =>
                `${p.cantidadDesde}${p.cantidadHasta === null ? "+" : `–${p.cantidadHasta}`}`,
            },
            {
              clave: "precio",
              ordenarPor: "precio",
              encabezado: "Precio",
              render: (p) => (
                <>
                  {precioUnitario(p.precio)}
                  {p.precioVip ? (
                    <span className="text-neutral-500"> · VIP {precioUnitario(p.precioVip)}</span>
                  ) : null}
                  {/* `es_importe_fijo` cambia el significado: es el total, no el unitario. */}
                  {p.esImporteFijo ? (
                    <span className="ml-2">
                      <Etiqueta tono="aviso">Importe fijo</Etiqueta>
                    </span>
                  ) : null}
                </>
              ),
            },
            // Desde y hasta en DOS columnas y el estado en la suya, como
            // `imprenta`. Juntas en una celda de `text-xs` la fecha de fin
            // quedaba pegada al chip de estado, y no se podía ordenar por
            // cuándo vence — que es la pregunta de «¿qué se me cae este mes?».
            {
              clave: "desde",
              ordenarPor: "desde",
              encabezado: "Vigente desde",
              className: "text-neutral-600",
              render: (p) => fecha(p.vigenteDesde),
            },
            {
              clave: "hasta",
              ordenarPor: "hasta",
              encabezado: "Vigente hasta",
              className: "text-neutral-600",
              // «Indefinido» es la palabra de `imprenta`; «sin fin» era nuestra.
              render: (p) => (p.vigenteHasta === null ? "Indefinido" : fecha(p.vigenteHasta)),
            },
            {
              clave: "estado",
              ordenarPor: "estado",
              encabezado: "Estado",
              render: (p) => <Etiqueta tono={ESTADOS[p.estado]}>{p.estado}</Etiqueta>,
            },
            {
              clave: "acciones",
              encabezado: "",
              className: "text-right",
              render: (p) => (
                <div className="flex justify-end gap-1">
                  {/* Las tres van sin texto, como en `imprenta` (ADR 0091): en
                    una fila que ya tiene seis columnas, un nombre que se repite
                    en cada renglón es ancho gastado. El nombre no se pierde —va
                    en `aria-label` para el lector de pantalla y en `title` para
                    el tooltip del mouse—, y esos dos son obligatorios acá. */}
                  <Boton
                    variante="fantasma"
                    tamano="icono"
                    aria-label={`Historial de ${p.tamano} · ${p.base}`}
                    title="Ver historial"
                    onClick={() => setHistorial(p)}
                  >
                    <History className="h-4 w-4" aria-hidden="true" />
                  </Boton>
                  {can("precios", "update") && p.estado === "vigente" && p.vigenteHasta === null ? (
                    <Boton
                      variante="fantasma"
                      tamano="icono"
                      aria-label={`Cerrar vigencia de ${p.tamano} · ${p.base}`}
                      title="Cerrar vigencia"
                      onClick={() => setCerrando(p)}
                    >
                      <CalendarOff className="h-4 w-4" aria-hidden="true" />
                    </Boton>
                  ) : null}
                  {/* Se ofrece sobre cualquier precio, no solo los programados,
                    porque es lo que hace `imprenta`: su `delete_precio` borra
                    sin mirar la vigencia. Restringirlo acá sería una regla
                    nueva escondida en el cliente — y con el worker permitiendo
                    igual, una garantía falsa (invariante 5). La advertencia de
                    qué se pierde va en la confirmación; si la restricción se
                    quiere de verdad, va al worker con su ADR — anotado en
                    `TODO.md`. */}
                  {can("precios", "delete") ? (
                    <Boton
                      variante="fantasma"
                      tamano="icono"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      aria-label={`Eliminar el precio de ${p.tamano} · ${p.base}`}
                      title="Eliminar"
                      onClick={() => setBorrando(p)}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Boton>
                  ) : null}
                </div>
              ),
            },
          ]}
        />
      )}

      {!mostrandoSinPrecio && visibles.length > 0 ? (
        <Paginacion
          pagina={paginaActual}
          tamano={tamanoPagina}
          total={ordenadas.length}
          alCambiarPagina={setPagina}
          alCambiarTamano={(t) => {
            setTamanoPagina(t);
            setPagina(1);
          }}
        />
      ) : null}

      {historial ? (
        <HistorialPrecios precio={historial} onCerrar={() => setHistorial(null)} />
      ) : null}

      {cerrando ? (
        <ModalCerrarVigencia
          precio={cerrando}
          onCerrar={() => setCerrando(null)}
          onConfirmar={async (hasta) => {
            await accion(() => cerrarVigencia(cerrando.id, hasta));
            setCerrando(null);
          }}
        />
      ) : null}

      <ConfirmModal
        abierto={borrando !== null}
        onCerrar={() => setBorrando(null)}
        onConfirmar={() => {
          if (borrando) void accion(() => borrarPrecio(borrando.id));
        }}
        titulo="Eliminar el precio"
        descripcion={
          borrando
            ? `${borrando.producto} · ${borrando.tamano} · ${borrando.base}, desde ${borrando.vigenteDesde}. ${
                borrando.estado === "futuro"
                  ? "Todavía no rigió, así que no hay presupuesto que dependa de él."
                  : "Se borra de verdad y no queda dado de baja. Los presupuestos que lo usaron conservan su importe congelado, pero la tarifa que lo explica desaparece."
              }`
            : ""
        }
        textoConfirmar="Eliminar"
        variante="destructiva"
      />

      {creando ? (
        <ModalNuevoPrecio
          onCerrar={() => setCreando(false)}
          onCreado={async () => {
            setCreando(false);
            await datos.recargar();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Cerrar la vigencia de un precio.
 *
 * Era un `window.prompt` pidiendo "YYYY-MM-DD": sin validación, sin decir qué
 * pasa después, y sin poder cancelar sin perder lo tipeado. Cerrar una tarifa es
 * de las pocas acciones que cambian lo que se le cobra a un cliente.
 */
function ModalCerrarVigencia({
  precio,
  onCerrar,
  onConfirmar,
}: {
  precio: PrecioListado;
  onCerrar: () => void;
  onConfirmar: (hasta: string) => Promise<void>;
}) {
  const [hasta, setHasta] = useState("");
  const [trabajando, setTrabajando] = useState(false);

  // No puede terminar antes de empezar: el worker lo rechaza, pero avisarlo
  // acá evita el viaje.
  const invalida = hasta !== "" && hasta < precio.vigenteDesde;

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo="Cerrar vigencia"
      descripcion={`${precio.producto} · ${precio.tamano} · ${precio.base}, rige desde ${precio.vigenteDesde}`}
    >
      <div className="space-y-3">
        <label className="block text-neutral-600 text-sm">
          <span>Último día en que rige</span>
          <input
            type="date"
            value={hasta}
            min={precio.vigenteDesde}
            onChange={(e) => setHasta(e.target.value)}
            className="mt-1 block rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
          />
        </label>
        {invalida ? <Aviso>No puede terminar antes de empezar.</Aviso> : null}
        <p className="text-neutral-500 text-sm">
          Desde el día siguiente, esta combinación no tiene precio hasta que se cargue otro.
        </p>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Boton variante="secundario" onClick={onCerrar}>
          Cancelar
        </Boton>
        <Boton
          disabled={trabajando || hasta === "" || invalida}
          onClick={async () => {
            setTrabajando(true);
            try {
              await onConfirmar(hasta);
            } finally {
              setTrabajando(false);
            }
          }}
        >
          {trabajando ? "Cerrando..." : "Cerrar vigencia"}
        </Boton>
      </div>
    </Modal>
  );
}

function ModalNuevoPrecio({
  onCerrar,
  onCreado,
}: {
  onCerrar: () => void;
  onCreado: () => Promise<void>;
}) {
  const productos = useDatos(() => fetchProductos());
  const [productoId, setProductoId] = useState("");
  const detalle = useDatos(
    () => (productoId ? fetchProducto(productoId) : Promise.resolve(null)),
    productoId,
  );
  const [form, setForm] = useState({
    tamanoId: "",
    baseId: "",
    cantidadDesde: "1",
    cantidadHasta: "",
    precio: "",
    precioVip: "",
    esImporteFijo: false,
    vigenteDesde: new Date().toISOString().slice(0, 10),
  });
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo="Nuevo precio"
      descripcion="Un precio no se edita: para cambiarlo se cierra el vigente y se abre otro"
    >
      <div className="space-y-3">
        {error ? <Aviso>{error}</Aviso> : null}

        <div className="space-y-1">
          <label htmlFor="producto" className="block text-neutral-600 text-sm">
            Producto
          </label>
          <select
            id="producto"
            value={productoId}
            onChange={(e) => {
              setProductoId(e.target.value);
              setForm((f) => ({ ...f, tamanoId: "", baseId: "" }));
            }}
            className="w-full rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
          >
            <option value="">— elegir —</option>
            {(productos.datos ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label htmlFor="tamano" className="block text-neutral-600 text-sm">
              Tamaño
            </label>
            <select
              id="tamano"
              value={form.tamanoId}
              onChange={(e) => setForm((f) => ({ ...f, tamanoId: e.target.value }))}
              className="w-full rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
            >
              <option value="">— elegir —</option>
              {(detalle.datos?.tamanos ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="base" className="block text-neutral-600 text-sm">
              Base
            </label>
            <select
              id="base"
              value={form.baseId}
              onChange={(e) => setForm((f) => ({ ...f, baseId: e.target.value }))}
              className="w-full rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
            >
              <option value="">— elegir —</option>
              {(detalle.datos?.bases ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.nombre}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Campo
            etiqueta="Cantidad desde"
            type="number"
            value={form.cantidadDesde}
            onChange={(e) => setForm((f) => ({ ...f, cantidadDesde: e.target.value }))}
          />
          <Campo
            etiqueta="Cantidad hasta"
            type="number"
            value={form.cantidadHasta}
            onChange={(e) => setForm((f) => ({ ...f, cantidadHasta: e.target.value }))}
            ayuda="Vacío = sin límite"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Campo
            etiqueta="Precio"
            type="number"
            value={form.precio}
            onChange={(e) => setForm((f) => ({ ...f, precio: e.target.value }))}
          />
          <Campo
            etiqueta="Precio VIP"
            type="number"
            value={form.precioVip}
            onChange={(e) => setForm((f) => ({ ...f, precioVip: e.target.value }))}
            ayuda="Opcional: sin esto, el VIP paga el precio normal"
          />
        </div>

        <Campo
          etiqueta="Vigente desde"
          type="date"
          value={form.vigenteDesde}
          onChange={(e) => setForm((f) => ({ ...f, vigenteDesde: e.target.value }))}
          ayuda="Puede ser a futuro: el precio arranca ese día"
        />
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Boton variante="secundario" onClick={onCerrar}>
          Cancelar
        </Boton>
        <Boton
          disabled={guardando || !form.tamanoId || !form.baseId || form.precio === ""}
          onClick={async () => {
            setGuardando(true);
            setError(null);
            try {
              await crearPrecio({
                tamanoId: form.tamanoId,
                baseId: form.baseId,
                cantidadDesde: Number(form.cantidadDesde),
                cantidadHasta: form.cantidadHasta === "" ? null : Number(form.cantidadHasta),
                precio: Number(form.precio),
                precioVip: form.precioVip === "" ? null : Number(form.precioVip),
                esImporteFijo: form.esImporteFijo,
                vigenteDesde: form.vigenteDesde,
              });
              await onCreado();
            } catch (err) {
              setError(err instanceof Error ? err.message : "No se pudo crear");
            } finally {
              setGuardando(false);
            }
          }}
        >
          {guardando ? "Guardando..." : "Crear"}
        </Boton>
      </div>
    </Modal>
  );
}

/**
 * Aumento masivo, con previsualización.
 *
 * La simulación usa el mismo endpoint que después aplica: lo que se muestra es
 * exactamente lo que va a pasar. Y si algún porcentaje deja precios en cero, el
 * servidor rechaza la operación entera antes de tocar nada.
 */
